import { useEffect, useRef, useCallback, useState } from 'react';
import api from '../utils/api';

type Props = {
  onSuccess: (payload: { token: string; user: any }) => void;
  className?: string;
};

declare global {
  interface Window {
    google?: any;
  }
}

export default function GoogleLoginButton({ onSuccess, className }: Props) {
  const btnRef = useRef<HTMLDivElement | null>(null);
  const [rendered, setRendered] = useState(false);
  const wantsDeepLink = typeof window !== 'undefined' && /[?&]return=teakonn(?:[&#]|$)/.test(window.location.search || '');
  const returnUrlParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search || '').get('returnUrl') : null;

  // Runtime sources in order of precedence
  // Resolve client ID based on environment (supports separate dev/prod IDs)
  const runtimeId =
    (typeof window !== 'undefined' && ((window as any).__GOOGLE_CLIENT_ID || localStorage.getItem('GOOGLE_CLIENT_ID'))) ||
    import.meta.env.VITE_GOOGLE_CLIENT_ID ||
    (import.meta.env as any).REACT_APP_GOOGLE_CLIENT_ID ||
    (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
      ? (import.meta.env.VITE_GOOGLE_CLIENT_ID_DEV as any) || (import.meta.env as any).REACT_APP_GOOGLE_CLIENT_ID_DEV
      : (import.meta.env.VITE_GOOGLE_CLIENT_ID_PROD as any) || (import.meta.env as any).REACT_APP_GOOGLE_CLIENT_ID_PROD) ||
    // Final safety fallback using provided ID to keep things clickable
    '50477811908-8hn3tlednkpntunkh5avh4ukh6mnffqa.apps.googleusercontent.com';

  const clientId = runtimeId;

  // Optional allowlist: hide Google button when current origin isn't authorized
  const allowedOriginsEnv =
    (import.meta.env as any).VITE_GOOGLE_ALLOWED_ORIGINS ||
    (import.meta.env as any).REACT_APP_GOOGLE_ALLOWED_ORIGINS ||
    '';
  const allowedOrigins = String(allowedOriginsEnv)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const currentOrigin = (typeof window !== 'undefined' && window.location.origin) || '';
  const originAllowed = allowedOrigins.length === 0 || allowedOrigins.includes(currentOrigin);
  const googleInitializedFlag = '__teakonnGoogleIdInitialized';

  const isGoogleInitialized = () => typeof window !== 'undefined' && Boolean((window as any)[googleInitializedFlag]);
  const markGoogleInitialized = () => {
    if (typeof window !== 'undefined') {
      (window as any)[googleInitializedFlag] = true;
    }
  };

  useEffect(() => {
    if (!clientId || !originAllowed) return; // Not configured or origin not allowed

    const ensureScript = () =>
      new Promise<void>((resolve, reject) => {
        if (window.google?.accounts?.id) return resolve();
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google script'));
        document.head.appendChild(script);
      });

    const initializeGoogle = () => {
      if (!window.google?.accounts?.id) return;
      if (!isGoogleInitialized()) {
        console.log('[GoogleLogin] GSI script loaded. Initializing…');
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: any) => {
            try {
              const idToken = response?.credential;
              if (!idToken) return;
              console.log('[GoogleLogin] Received credential. Exchanging with backend…');
              const { data } = await api.post('/auth/google', { idToken });
              const { token, user } = data;
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(user));
              // Prefer explicit returnUrl when provided (Expo Go exp://..., dev client teakonn://...)
              if (returnUrlParam) {
                try {
                  window.location.replace(returnUrlParam);
                  return;
                } catch {}
              }
              // Otherwise, if app requested legacy deep link, redirect to native scheme
              if (wantsDeepLink) {
                try {
                  const dl = `teakonn://auth?token=${encodeURIComponent(token)}`;
                  // Prefer replace to avoid browser history pollution
                  window.location.replace(dl);
                  return;
                } catch {}
              }
              onSuccess({ token, user });
            } catch (e) {
              // Surface backend error details & common origin mismatch guidance
              const detail = e?.response?.data || e?.message || e;
              console.error('[GoogleLogin] Exchange failed:', detail);
              try {
                const msg = String(detail?.message || detail || '').toLowerCase();
                if (msg.includes('client mismatch') || msg.includes('origin')) {
                  alert('Google sign-in failed due to OAuth client/origin mismatch. Ensure this site\'s origin is added under Authorized JavaScript origins for your Web OAuth client, and the frontend uses the matching client ID.');
                }
              } catch {}
            }
          },
        });
        markGoogleInitialized();
      }
      if (btnRef.current && !rendered) {
        window.google.accounts.id.renderButton(btnRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          width: 320,
        });
        setRendered(true);
        console.log('[GoogleLogin] Rendered Google button.');
      }
    };

    ensureScript()
      .then(initializeGoogle)
      .catch((e) => console.error(e));
  }, [clientId, originAllowed, onSuccess, returnUrlParam, wantsDeepLink, rendered]);

  const bootstrapFallback = useCallback(async () => {
    try {
      console.log('[GoogleLogin] Fallback click. Using client ID:', runtimeId);
      const ensureScript = () =>
        new Promise<void>((resolve, reject) => {
          if (window.google?.accounts?.id) return resolve();
          const script = document.createElement('script');
          script.src = 'https://accounts.google.com/gsi/client';
          script.async = true;
          script.defer = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('Failed to load Google script'));
          document.head.appendChild(script);
        });
      await ensureScript();
      if (!isGoogleInitialized()) {
        window.google.accounts.id.initialize({
          client_id: runtimeId,
          callback: async (response: any) => {
            try {
              const idToken = response?.credential;
              if (!idToken) {
                console.warn('[GoogleLogin] No credential returned from Google.');
                return;
              }
              console.log('[GoogleLogin] Fallback credential received. Exchanging…');
              const { data } = await api.post('/auth/google', { idToken });
              const { token, user } = data;
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(user));
              if (returnUrlParam) {
                try {
                  window.location.replace(returnUrlParam);
                  return;
                } catch {}
              }
              if (wantsDeepLink) {
                try {
                  const dl = `teakonn://auth?token=${encodeURIComponent(token)}`;
                  window.location.replace(dl);
                  return;
                } catch {}
              }
              onSuccess({ token, user });
            } catch (e: any) {
              console.error('[GoogleLogin] Fallback exchange failed:', e?.response?.data || e?.message || e);
              try {
                const msg = String(e?.response?.data?.message || e?.message || '').toLowerCase();
                if (msg.includes('client mismatch') || msg.includes('origin')) {
                  alert('Google sign-in failed due to OAuth client/origin mismatch. Add this origin to Authorized JavaScript origins and use the matching client ID.');
                }
              } catch {}
            }
          },
        });
        markGoogleInitialized();
      }
      if (btnRef.current && !rendered) {
        window.google.accounts.id.renderButton(btnRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          width: 320,
        });
        setRendered(true);
        console.log('[GoogleLogin] Fallback rendered Google button.');
      }
      try {
        window.google.accounts.id.prompt();
      } catch {
        // prompt may be blocked; rely on button interaction
      }
    } catch (e) {
      console.error('[GoogleLogin] Fallback bootstrap failed:', e);
    }
  }, [runtimeId, rendered, onSuccess]);

  if (!clientId || !originAllowed) {
    return (
      <div className={className}>
        <button
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '10px 16px',
            borderRadius: 9999,
            border: '1px solid var(--border, #334155)',
            background: 'var(--card, #0f172a)',
            color: 'var(--text, #e2e8f0)',
            opacity: 1,
            width: 320,
            cursor: 'pointer',
          }}
          title={originAllowed ? 'Using runtime client ID fallback; click to initialize' : `Google sign-in unavailable for origin: ${currentOrigin}`}
          onClick={bootstrapFallback}
          disabled={!originAllowed}
        >
          {originAllowed ? 'Continue with Google' : 'Google sign-in unavailable'}
        </button>
        <div ref={btnRef} style={{ height: 0, overflow: 'hidden' }} />
        {!originAllowed && (
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
            Add this origin in Google Cloud Console → OAuth client → Authorized JavaScript origins: <strong>{currentOrigin}</strong>
          </div>
        )}
      </div>
    );
  }

  return <div ref={btnRef} className={className} />;
}
